import { useState, useRef } from "react";
import { useQueryParam, DelimitedArrayParam } from "use-query-params";
import {
  StoreType,
  StoreLabelType,
  OilProductType,
  OilProductUiType,
  StoreLabelUiType,
  GeolocationStateType
} from "./constants";

import { MapRefType } from "./components/Map";
import useTranslate from "../../hooks/use-translate";
import { getDefaultViewport } from "./components/Map/utils";

/**
 * Extract unique list of labels associated to a list of stores. Used for labels filter in top bar.
 */
export const getAllLabelsFromStores = (
  stores: StoreType[]
): StoreLabelType[] => {
  return Object.values(
    stores
      .reduce((accumulator, store) => {
        return [...accumulator, ...store.labels];
      }, [])
      .reduce((accumulator, label) => {
        return {
          ...accumulator,
          [label.originalId]: label
        };
      }, {})
  );
};

/**
 * Get list of stores filtered using current query parameters applied by UI filters.
 */
export const useFilteredStores = (stores: StoreType[]): StoreType[] => {
  let filteredStores = [...stores];

  // Apply labels filter
  const [selectedLabels] = useQueryParam("labels", DelimitedArrayParam);
  if (selectedLabels && selectedLabels.length > 0) {
    filteredStores = filteredStores.filter(store =>
      selectedLabels.every(label => store.labels.find(l => l.text === label))
    );
  }

  // Apply Oils filter
  const [selectedProducts] = useQueryParam("products", DelimitedArrayParam);
  if (selectedProducts && selectedProducts.length > 0) {
    filteredStores = filteredStores.filter(store =>
      selectedProducts.every(productSlug =>
        store.availableProducts.find(p => p.slug === productSlug)
      )
    );
  }

  return filteredStores;
};

/**
 * Remove oils that are not available in any stores
 */
export const getOilsAvailableInStores = (
  oils: OilProductType[],
  stores: StoreType[]
) => {
  return oils.filter(oil => {
    return stores.find(store =>
      store.availableProducts.find(p => p.slug === oil.slug)
    );
  });
};

/**
 * Labels filter re-usable logic
 */
export const useLabelsFilter = (labels: StoreLabelType[]) => {
  const [selectedLabels, setSelectedLabels] = useQueryParam(
    "labels",
    DelimitedArrayParam
  );

  const handleLabelFilterClick = (label: StoreLabelType) => {
    const values = selectedLabels || [];
    const isAlreadySelected = values.includes(label.text);
    if (isAlreadySelected) {
      setSelectedLabels(values.filter(l => l !== label.text));
    } else {
      setSelectedLabels([...values, label.text]);
    }
  };

  const filterLabels: StoreLabelUiType[] = labels.map(label => ({
    ...label,
    isSelected: (selectedLabels || []).includes(label.text)
  }));

  return {
    filterLabels,
    handleLabelFilterClick
  };
};

/**
 * Oils filter re-usable logic
 */
export const useOilsFilter = (oils: OilProductType[]) => {
  const [selectedOils, setSelectedOils] = useQueryParam(
    "products",
    DelimitedArrayParam
  );

  const handleOilFilterClick = (oil: OilProductType) => {
    const values = selectedOils || [];
    const isAlreadySelected = values.includes(oil.slug);
    if (isAlreadySelected) {
      setSelectedOils(values.filter(l => l !== oil.slug));
    } else {
      setSelectedOils([...values, oil.slug]);
    }
  };

  const filterOils: OilProductUiType[] = oils.map(oil => ({
    ...oil,
    isSelected: (selectedOils || []).includes(oil.slug)
  }));

  return {
    filterOils,
    handleOilFilterClick
  };
};

/**
 * Logic related to "Use my location" feature
 */
export const useMapGeolocation = () => {
  const t = useTranslate();

  const [geolocationState, setGeolocationState] = useState<
    GeolocationStateType
  >("inactive");
  const mapRef = useRef<MapRefType>();

  const handleGeolocationChange = ({ coords }) => {
    setGeolocationState("active");
    if (
      mapRef &&
      mapRef.current &&
      mapRef.current.zoomToCoordinatesWithFitBounds
    ) {
      mapRef.current.zoomToCoordinatesWithFitBounds(coords);
    }
  };

  const handleGeolocationError = () => {
    setGeolocationState("inactive");
  };

  const requestGeolocation = () => {
    if (geolocationState === "active") {
      setGeolocationState("inactive");
      if (mapRef && mapRef.current && mapRef.current.setViewport) {
        mapRef.current.setViewport(getDefaultViewport());
      }
      return;
    }

    const geo = window.navigator.geolocation;
    if (!geo) {
      return;
    }
    setGeolocationState("loading");
    geo.getCurrentPosition(handleGeolocationChange, handleGeolocationError);
  };

  return { mapRef, geolocationState, requestGeolocation };
};
